import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faArrowRightArrowLeft, faPen } from '@fortawesome/pro-solid-svg-icons';
import type { FC, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import {
  PlayerFieldStatus,
  Position,
  ScoreStatus,
  So5FixtureEvent,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { OnField } from '@sorare/core/src/atoms/icons/OnField';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { OverlapItems } from '@sorare/core/src/atoms/ui/OverlapItems';
import { PlayerAppearance } from '@sorare/core/src/components/cards/PlayerAppearance';
import { LiveDot } from '@sorare/core/src/components/live/LiveDot';
import { StatIcon } from '@sorare/core/src/components/stats/StatIcon';
import { range } from '@sorare/core/src/lib/arrays';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType, isTypeFn } from '@sorare/core/src/lib/gql';
import type { Stats } from '@sorare/core/src/lib/scoring';

import { PlayerGameStatsPrediction } from '@sorare/marketplace/src/components/game/PlayerGameStatsPrediction';
import { Grade } from '@sorare/marketplace/src/components/player/Grade';
import { InjuryTag } from '@sorare/marketplace/src/components/player/InjuryTag';
import {
  isGameLive,
  isGameScheduled,
} from '@sorare/marketplace/src/shared/game/lib';
import { SubIcon } from '@sorare/marketplace/src/shared/stats/SubIcon';
import { getDecisiveActions } from '@sorare/marketplace/src/shared/stats/lib';

import { So5AppearanceScore } from 'components/appearance/So5AppearanceScore';
import { Captain } from 'components/so5/Captain';
import { So5AppearenceInlineGames } from 'components/stats/So5AppearenceInlineGames';
import { So5AppearenceInlineGamesDots } from 'components/stats/So5AppearenceInlineGamesDots';
import {
  getBestEligibleProjection,
  isAppearanceLocked,
  isFixtureLive,
  isFixtureOpened,
} from 'lib/so5';

import type { PlayerCard_so5Appearance } from './__generated__/index.graphql';

const Wrapper = styled.div`
  position: relative;
  isolation: isolate;
`;

const EditIconWrapper = styled.div`
  position: absolute;
  top: calc(-1 * var(--unit));
  right: calc(-1 * var(--unit));
  z-index: 1;
`;

const DefaultFooter = styled(Vertical).attrs({ center: true })``;

type Props = {
  onClick?: () => void;
  appearance: PlayerCard_so5Appearance;
  showScore?: boolean;
  withDate?: boolean;
  withInlineGamesDots?: boolean;
  Footer?: FC<{ children: ReactNode }>;
  displayLiveEdit?: boolean;
  editPath?: string;
};

export const PlayerCard = ({
  onClick,
  appearance,
  showScore,
  withDate,
  withInlineGamesDots,
  Footer,
  displayLiveEdit,
  editPath,
}: Props) => {
  const { formatMessage } = useIntl();

  const { eligiblePlayerGameScores, captain, anyPlayer, lockedForLeaderboard } =
    appearance;

  const anyGameLive = eligiblePlayerGameScores.some(pgs =>
    isGameLive(pgs.anyGame.status)
  );

  const isLive = isFixtureLive(appearance.so5Fixture);
  const isUpcoming = isFixtureOpened(appearance.so5Fixture);

  // the player card may be SP but the player may be a relief pitcher today
  // they can still be used and score pitching points in the SP slot but they are in fact a relief pitcher
  const isStartingPitcher =
    eligiblePlayerGameScores.some(
      pgs => pgs.positionTyped === Position.baseball_starting_pitcher
    ) && anyPlayer.anyPositions.includes(Position.baseball_starting_pitcher);

  const pitchingGames = eligiblePlayerGameScores.filter(
    pgs =>
      isType(pgs.anyGame, 'GameOfBaseball') &&
      pgs.anyGame.probablePitchers.find(p => p.slug === anyPlayer.slug)
  );

  const FooterWrapper = Footer || DefaultFooter;

  const hidePrediction = appearance.scoreStatus === ScoreStatus.REVIEWING;

  // player is technically not DNP but we can confidently assume they will be
  const scoreIsPendingButWillBeDNP =
    isStartingPitcher && pitchingGames.length === 0;

  const footballPlayerGameStatsWithOdds = eligiblePlayerGameScores
    .filter(({ anyGame }) => isGameScheduled(anyGame.status))
    .map(pgs => pgs.anyPlayerGameStats)
    .filter(isTypeFn('PlayerGameStats'))
    .find(pgstats => pgstats.footballPlayingStatusOdds);

  const uniquePlayerGameScore = appearance.playerGameScore;
  const decisiveActions = uniquePlayerGameScore
    ? getDecisiveActions(uniquePlayerGameScore)
    : [];
  const hasDecisiveActions = Object.values(decisiveActions).some(Boolean);

  const uniqueFootballPlayerGameStats = isType(
    uniquePlayerGameScore?.anyPlayerGameStats,
    'PlayerGameStats'
  )
    ? uniquePlayerGameScore.anyPlayerGameStats
    : null;

  const substitutedOut =
    uniqueFootballPlayerGameStats?.fieldStatus ===
    PlayerFieldStatus.SUBSTITUTED;
  const substitutedIn =
    uniqueFootballPlayerGameStats?.fieldStatus === PlayerFieldStatus.ON_FIELD &&
    !uniqueFootballPlayerGameStats.gameStarted;

  const isLocked = isAppearanceLocked(appearance);
  const canBeSwapped =
    isLive &&
    !isLocked &&
    appearance.so5Fixture.eventType.toUpperCase() === So5FixtureEvent.DAILY;

  const projection = getBestEligibleProjection(eligiblePlayerGameScores);

  return (
    <Wrapper>
      <PlayerAppearance
        onClick={onClick}
        disabled={!eligiblePlayerGameScores.length}
        displayLocked={!!displayLiveEdit && Boolean(isLocked)}
        appearance={appearance}
        captain={!!captain && <Captain active size={2} />}
        topBadge={
          <>
            {!!projection && !!isUpcoming && (
              <Grade size={2.5} projection={projection} />
            )}
            {!!substitutedOut && <SubIcon type="sub_out" />}
            {!!substitutedIn && <SubIcon type="sub_in" />}
            {uniquePlayerGameScore?.scoreStatus === ScoreStatus.REVIEWING &&
              isGameScheduled(uniquePlayerGameScore.anyGame.status) &&
              uniqueFootballPlayerGameStats?.fieldStatus ===
                PlayerFieldStatus.ON_FIELD && <OnField />}
          </>
        }
        bottomBadge={
          <Horizontal center>
            {!!(isUpcoming || isLive) && <InjuryTag player={anyPlayer} />}
            {!isUpcoming && !!hasDecisiveActions && (
              <OverlapItems offset={8}>
                {Object.entries(decisiveActions).map(([key, value]) =>
                  range(value || 0).map(i => (
                    <StatIcon key={`${key}-${i}`} statKey={key as Stats} />
                  ))
                )}
              </OverlapItems>
            )}
            {!lockedForLeaderboard && !hidePrediction && (
              <Vertical>
                {!!footballPlayerGameStatsWithOdds?.footballPlayingStatusOdds && (
                  <PlayerGameStatsPrediction
                    variant="chip"
                    playingStatusOdds={
                      footballPlayerGameStatsWithOdds.footballPlayingStatusOdds
                    }
                  />
                )}
              </Vertical>
            )}
          </Horizontal>
        }
        score={
          <FooterWrapper>
            <Horizontal gap={0.5}>
              {!showScore && !!anyGameLive && !withInlineGamesDots && (
                <LiveDot size="sm" animate />
              )}
              {!!showScore && (
                <Horizontal gap={0.5}>
                  {!!anyGameLive && !withInlineGamesDots && (
                    <LiveDot size="sm" animate />
                  )}
                  {canBeSwapped ? (
                    <Chip size="large">
                      <FontAwesomeIcon icon={faArrowRightArrowLeft} />
                    </Chip>
                  ) : (
                    <So5AppearanceScore
                      hideReviewing
                      withDate={withDate}
                      so5Appearance={appearance}
                      forceScoreStatus={
                        scoreIsPendingButWillBeDNP
                          ? ScoreStatus.DID_NOT_PLAY
                          : undefined
                      }
                    />
                  )}
                </Horizontal>
              )}
            </Horizontal>
            {withInlineGamesDots ? (
              <So5AppearenceInlineGamesDots
                isStartingPitcher={isStartingPitcher}
                sizeUnit={anyPlayer.sport === Sport.BASEBALL ? 'md' : 'lg'}
                pitchingGames={pitchingGames}
                so5Appearance={appearance}
              />
            ) : (
              <So5AppearenceInlineGames so5Appearance={appearance} />
            )}
          </FooterWrapper>
        }
      />
      {!!displayLiveEdit && !isLocked && (
        <EditIconWrapper>
          <IconButton
            size="compact"
            color="tertiary"
            aria-label={formatMessage(glossary.edit)}
            to={editPath}
          >
            <FontAwesomeIcon icon={faPen} size="xs" />
          </IconButton>
        </EditIconWrapper>
      )}
    </Wrapper>
  );
};

PlayerCard.fragments = {
  so5Appearance: gql`
    fragment PlayerCard_so5Appearance on So5AppearanceInterface {
      id
      captain
      pictureUrl
      lockedForLeaderboard
      anyPlayer {
        slug
        sport
        ...InjuryTag_anyPlayer
        anyPositions
      }
      playerGameScore {
        id
        scoreStatus
        anyGame {
          id
          status: statusTyped
        }
        anyPlayerGameStats {
          id
          ... on PlayerGameStats {
            id
            gameStarted
            fieldStatus
          }
        }
        ...getDecisiveActions_playerGameScoreInterface
      }
      eligiblePlayerGameScores {
        id
        positionTyped
        anyPlayerGameStats {
          id
          ... on PlayerGameStats {
            id
            footballPlayingStatusOdds {
              reliability
              ...PlayerGameStatsPrediction_playingStatusOdds
            }
          }
        }
        anyGame {
          id
          status: statusTyped
          ... on GameOfBaseball {
            id
            probablePitchers {
              slug
            }
          }
        }
        projection {
          id
          ...Grade_playerGameScoreProjection
        }
        ...getBestProjection_playerGameScoreInterface
      }
      so5Fixture {
        slug
        eventType
        ...isFixtureOpened_so5Fixture
        ...isFixtureLive_so5Fixture
      }
      ...So5AppearanceScore_so5Appearance
      ...So5AppearenceInlineGames_so5Appearance
      ...So5AppearenceInlineGamesDots_so5Appearance
      ...PlayerAppearance_so5Appearance
      ...isAppearanceLocked_so5Appearance
    }
    ${So5AppearanceScore.fragments.so5Appearance}
    ${So5AppearenceInlineGames.fragments.so5Appearance}
    ${So5AppearenceInlineGamesDots.fragments.so5Appearance}
    ${InjuryTag.fragments.anyPlayer}
    ${getDecisiveActions.fragments.playerGameScoreInterface}
    ${PlayerGameStatsPrediction.fragments.playingStatusOdds}
    ${PlayerAppearance.fragments.so5Appearance}
    ${isAppearanceLocked.fragments.so5Appearance}
    ${isFixtureLive.fragments.so5Fixture}
    ${isFixtureOpened.fragments.so5Fixture}
    ${Grade.fragments.projection}
    ${getBestEligibleProjection.fragments.playerGameScoreInterface}
  ` as TypedDocumentNode<PlayerCard_so5Appearance>,
};
